import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { deleteManualPaymentApi } from "../../../utils/api/manualPayment";
import { PaymentHistoryItem } from "../../../redux/types/types";
import Notiflix from "notiflix";

interface DeleteManualPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  payment: PaymentHistoryItem | null;
  onSuccess?: () => void;
}

export default function DeleteManualPaymentDialog({
  open,
  onClose,
  payment,
  onSuccess,
}: DeleteManualPaymentDialogProps) {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatPrice = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDeletePayment = async () => {
    if (!payment) return;

    setLoading(true);
    setError(null);

    try {
      await deleteManualPaymentApi(payment.id);

      Notiflix.Notify.success(
        intl.formatMessage({ id: "admin.paymentHistory.deleteSuccess" })
      );

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Failed to delete manual payment:", error);
      setError(intl.formatMessage({ id: "admin.paymentHistory.deleteError" }));
      Notiflix.Notify.failure(
        intl.formatMessage({ id: "admin.paymentHistory.deleteError" })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="warning" />
          <FormattedMessage
            id="admin.paymentHistory.deletePayment"
            defaultMessage="Delete Manual Payment"
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          <FormattedMessage
            id="admin.paymentHistory.deleteConfirmation"
            defaultMessage="Are you sure you want to delete this manual payment? This action cannot be undone."
          />
        </Typography>

        {payment && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "error.light",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "error.main",
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              <FormattedMessage
                id="admin.paymentHistory.paymentDetails"
                defaultMessage="Payment Details"
              />
            </Typography>
            <Typography variant="body2">
              <FormattedMessage
                id="admin.paymentHistory.attendee"
                defaultMessage="Attendee"
              />
              :{" "}
              {`${payment.attendee.firstName} ${payment.attendee.lastName} (${payment.attendee.email})`}
            </Typography>
            <Typography variant="body2">
              <FormattedMessage id="manualPayment.amount" />:{" "}
              {formatPrice(payment.amount)} {payment.currency}
            </Typography>
            {payment.numberOfClasses && (
              <Typography variant="body2">
                <FormattedMessage id="manualPayment.classes" />:{" "}
                {payment.numberOfClasses}
              </Typography>
            )}
            {payment.specialType && (
              <Typography variant="body2">
                <FormattedMessage id="manualPayment.specialType" />:{" "}
                {payment.specialType}
              </Typography>
            )}
            <Typography variant="body2">
              <FormattedMessage id="admin.paymentHistory.date" />:{" "}
              {new Date(payment.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
        )}

        <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
          <FormattedMessage
            id="admin.paymentHistory.deleteWarning"
            defaultMessage="Note: This will also deduct the associated classes from the attendee's remaining classes."
          />
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="secondary" disabled={loading}>
          <FormattedMessage id="manualPayment.cancel" />
        </Button>
        <Button
          onClick={handleDeletePayment}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <WarningIcon />}
        >
          <FormattedMessage
            id="admin.paymentHistory.confirmDelete"
            defaultMessage="Delete Payment"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
